import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Shared auth shell + field components for features/auth screens.
 * Kept local to the feature (T1's generic primitives don't exist yet); split
 * into components/feedback when T1 lands if the shapes match.
 */

export function AuthShell({ eyebrow, title, subtitle, children, footer }) {
  return (
    <main className="auth-screen">
      <div className="auth-card">
        <div className="auth-brand" aria-hidden="true">
          <div className="auth-brand-mark"><span /></div>
          <div><strong>AGI</strong><small>AGENCY OPS</small></div>
        </div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {subtitle && <p className="auth-subtitle">{subtitle}</p>}
        {children}
        {footer && <div className="auth-footer">{footer}</div>}
      </div>
      <p className="auth-legal">
        Protected by human-in-the-loop governance. By continuing you accept the
        <Link to="/login"> terms of service</Link> and{' '}
        <Link to="/login">privacy policy</Link>.
      </p>
    </main>
  );
}

export function AuthField({
  id,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  autoComplete,
  required = true,
  hint
}) {
  return (
    <div className="auth-field">
      <label htmlFor={id}>{label}</label>
      <input
        autoComplete={autoComplete}
        id={id}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        required={required}
        type={type}
        value={value}
      />
      {hint && <small>{hint}</small>}
    </div>
  );
}

export function AuthError({ message }) {
  if (!message) return null;
  return (
    <div className="auth-error" role="alert">
      {message}
    </div>
  );
}

export function AuthSubmit({ busy, children }) {
  return (
    <button className="auth-submit" disabled={busy} type="submit">
      {busy ? 'Working…' : children}
    </button>
  );
}

export function PasswordStrengthMeter({ password }) {
  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password) || /[^A-Za-z0-9]/.test(password),
    /\d/.test(password)
  ];
  const score = password ? checks.filter(Boolean).length : 0;
  const labels = ['Too short', 'Weak', 'Good', 'Strong'];

  return (
    <div className={`password-strength strength-${score}`}>
      <div className="password-strength-bars" aria-hidden="true">
        <span /><span /><span />
      </div>
      <small aria-live="polite">{password ? labels[score] : 'Use 8+ characters, a number, and a capital or symbol.'}</small>
    </div>
  );
}
